import { type Dispatch, type SetStateAction } from "react";
import { useForm } from "@tanstack/react-form";
import { Schema } from "effect";
import { kioskOperationsClient } from "../../../../../lib/rpc/kiosk-operations.client";
import { frontdeskDiagnosticLogger } from "../../../../../lib/diagnostic-logger";
import {
  formatDateOfBirthForDisplay,
  normalizeDateOfBirthForApi,
} from "../../../components/DateOfBirthInput";
import type { PatientData } from "../machine";
import type { useSchedulerSend } from "../context";
import { CreatePatientSchema, type CreatePatientData } from "../schema";

type SchedulerSend = ReturnType<typeof useSchedulerSend>;

const EMPTY_CREATE_PATIENT: CreatePatientData = {
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  insurance: "",
};

/**
 * Creation outcome shown inline on the create form (no toast, KO-02).
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const status: CreatePatientStatus = "idle";
 * ```
 */
export type CreatePatientStatus = "idle" | "creating" | "failed";

/**
 * Creates the scheduler patient-create form and submits new patients to the
 * bridge. The insurance has no preselection: the patient must choose it
 * explicitly (KO-71). Failures are reported through `setStatus` and rendered
 * inline by the step.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * const form = useSchedulerCreatePatientForm({
 *   send,
 *   setStatus,
 *   onPolicyRestricted: () => undefined,
 * })
 * ```
 */
export function useSchedulerCreatePatientForm({
  send,
  setStatus,
  onPolicyRestricted,
}: {
  send: SchedulerSend;
  setStatus: Dispatch<SetStateAction<CreatePatientStatus>>;
  onPolicyRestricted: () => void;
}) {
  return useForm({
    defaultValues: EMPTY_CREATE_PATIENT,
    validators: {
      onChange: Schema.toStandardSchemaV1(CreatePatientSchema),
    },
    onSubmit: async ({ value }) => {
      const insurance = value.insurance;
      if (insurance === "") return;
      setStatus("creating");
      try {
        const normalizedDateOfBirth = normalizeDateOfBirthForApi(
          value.dateOfBirth,
        );
        const outcome = await kioskOperationsClient.createPatient({
          firstname: value.firstName,
          lastname: value.lastName,
          dob: formatDateOfBirthForDisplay(normalizedDateOfBirth),
          gender: "UNKNOWN",
          talk: "NONE",
          insurance,
        });

        if (outcome.kind === "policy-restricted") {
          setStatus("idle");
          onPolicyRestricted();
          return;
        }

        if (outcome.kind === "failed") {
          frontdeskDiagnosticLogger.error("Kiosk patient creation failed", {
            outcome: "creation-failed",
            insurance,
          });
          setStatus("failed");
          return;
        }

        const patient: PatientData = {
          id: outcome.patientId,
          firstName: value.firstName,
          lastName: value.lastName,
          dateOfBirth: normalizedDateOfBirth,
          insurance,
        };
        setStatus("idle");
        send({ type: "PATIENT_IDENTIFIED", patient });
      } catch {
        frontdeskDiagnosticLogger.error("Kiosk patient creation failed", {
          outcome: "client-defect",
          insurance,
        });
        setStatus("failed");
      }
    },
  });
}

/**
 * Form API returned by useSchedulerCreatePatientForm.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * type CreateForm = SchedulerCreatePatientForm
 * ```
 */
export type SchedulerCreatePatientForm = ReturnType<
  typeof useSchedulerCreatePatientForm
>;
